import { forwardRef, useMemo } from 'react';
import {
  PiCaretDoubleDownDuotone,
  PiCaretDoubleLeftDuotone,
  PiCaretDoubleRightDuotone,
  PiCaretDoubleUpDuotone,
} from 'react-icons/pi';

import { styled } from '@spazious/dollhouse-theme';
import {
  Flex,
  FlexElement,
  FlexProps,
  IconButton,
  Menu,
  MenuPanel,
  MenuProps,
} from '@spazious/dollhouse-uikit';

import { useDeviceInfo } from '../../hooks/index.js';
import { MenuDialogButtons } from '../MenuDialogButtons/index.js';
import { MenuNavButtons } from '../MenuNavButtons/index.js';

export interface SideMenuMobileProps extends Omit<MenuProps, 'direction'> {
  open: boolean;
  isPortrait: boolean;
}

export interface ShowSideMenuIconProps extends Omit<FlexProps, 'onClick'> {
  open: boolean;
  onClick: () => void;
  icons: {
    open: React.ReactNode;
    closed: React.ReactNode;
  };
  isPortrait: boolean;
}

const OpenIconButton = styled(IconButton)`
  background-color: var(--spz-sys-color-surface-container);
`;

// @Liouch why we need to wrap this in a Flex component?
const ShowSideMenuIcon = styled(
  forwardRef<FlexElement, ShowSideMenuIconProps>(({ onClick, open, icons, ...restProps }) => {
    return (
      <Flex {...restProps}>
        <OpenIconButton onClick={onClick}>{open ? icons.open : icons.closed}</OpenIconButton>
      </Flex>
    );
  })
)`
  z-index: 1;
  position: absolute;
  ${({ isPortrait }) =>
    isPortrait
      ? 'top: -20px; left: 50%; transform: translateX(-50%)'
      : 'right: -20px; top: 50%; transform: translateY(-50%)'};
`;

const arrowIconMapping = {
  portrait: {
    open: <PiCaretDoubleDownDuotone />,
    closed: <PiCaretDoubleUpDuotone />,
  },
  landscape: {
    open: <PiCaretDoubleLeftDuotone />,
    closed: <PiCaretDoubleRightDuotone />,
  },
};

const directionMapping = {
  top: 'row-reverse',
  right: 'column-reverse',
  bottom: 'row',
  left: 'column',
};

export const SideMenuMobile = styled(
  ({ open = true, onClick, ...restProps }: SideMenuMobileProps) => {
    const isPortrait = useDeviceInfo((state) => state.isPortrait);

    const sideMenuDirection = directionMapping[
      isPortrait ? 'bottom' : 'left'
    ] as FlexProps['direction'];

    const icons = useMemo(
      () => (isPortrait ? arrowIconMapping.portrait : arrowIconMapping.landscape),
      [isPortrait]
    );

    const semiClosedSize = '20px';

    const openHeight = isPortrait ? 'auto' : '100%';
    const closedHeight = isPortrait ? semiClosedSize : '100%';
    const openWidth = isPortrait ? '100%' : 'auto';
    const closedWidth = isPortrait ? '100%' : semiClosedSize;

    const height = open ? openHeight : closedHeight;
    const width = open ? openWidth : closedWidth;

    return (
      <Menu
        direction={sideMenuDirection}
        height={height}
        width={width}
        {...restProps}
        position="relative"
      >
        <MenuPanel>
          {open && <MenuNavButtons buttonSize="small" direction={sideMenuDirection} />}
        </MenuPanel>
        <ShowSideMenuIcon
          icons={icons}
          isPortrait={isPortrait}
          open={open}
          onClick={onClick as any}
        />
        <MenuPanel>
          {open && <MenuDialogButtons buttonSize="small" direction={sideMenuDirection} />}
        </MenuPanel>
      </Menu>
    );
  }
).withConfig({
  displayName: 'SideMenuMobile',
})`
  transition: background-color 0.3s ease-in-out;
  background-color: var(--spz-sys-color-surface-container);
  // overflow: hidden;
`;
